import { cn } from '@/lib/utils'

type FlagCountry = 
  | 'th' | 'en' | 'lo' | 'km'  // Language codes
  | 'la' | 'kh'                 // Country codes (aliases)
  | 'mm' | 'vn' | 'us'          // Additional countries

interface FlagIconProps {
  country: FlagCountry
  className?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

export function FlagIcon({ country, className, size = 'sm' }: FlagIconProps) {
  const sizeClasses = {
    xs: 'w-4 h-3',
    sm: 'w-6 h-4',
    md: 'w-8 h-6',
    lg: 'w-12 h-8',
  }

  // Map aliases
  const aliasMap: Record<string, string> = {
    la: 'lo',  // Laos
    kh: 'km',  // Cambodia
  }
  const countryCode = aliasMap[country] || country

  const flags: Record<string, React.ReactNode> = {
    // Thailand 🇹🇭 (แดง-ขาว-น้ำเงิน-ขาว-แดง)
    th: (
      <svg viewBox="0 0 900 600" className={cn(sizeClasses[size], "rounded-sm shadow-sm", className)}>
        <rect width="900" height="600" fill="#FFFFFF"/>
        <rect width="900" height="100" fill="#ED1C24"/>
        <rect y="500" width="900" height="100" fill="#ED1C24"/>
        <rect y="100" width="900" height="100" fill="#FFFFFF"/>
        <rect y="400" width="900" height="100" fill="#FFFFFF"/>
        <rect y="200" width="900" height="200" fill="#241D4F"/>
      </svg>
    ),

    // United Kingdom 🇬🇧
    en: (
      <svg viewBox="0 0 640 480" className={cn(sizeClasses[size], "rounded-sm shadow-sm", className)}>
        <path fill="#012169" d="M0 0h640v480H0z"/>
        <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/>
        <path fill="#C8102E" d="m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
        <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
        <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
      </svg>
    ),

    // Laos 🇱🇦
    lo: (
      <svg viewBox="0 0 640 480" className={cn(sizeClasses[size], "rounded-sm shadow-sm", className)}>
        <path fill="#ce1126" d="M0 0h640v480H0z"/>
        <path fill="#002868" d="M0 119.4h640v241.2H0z"/>
        <circle cx="320" cy="240" r="90" fill="#fff"/>
      </svg>
    ),

    // Cambodia 🇰🇭
    km: (
      <svg viewBox="0 0 640 480" className={cn(sizeClasses[size], "rounded-sm shadow-sm", className)}>
        <path fill="#032ea1" d="M0 0h640v480H0z"/>
        <path fill="#e00025" d="M0 120h640v240H0z"/>
        <g fill="#fff" transform="matrix(1.2 0 0 1.2 128 142.5)">
          <path d="M160 65v15h-10V65zm50 0v15h-10V65zm-25 0v15h-10V65z"/>
          <path d="M170 80v30h-10V80zm-30 0v30h-10V80zm60 0v30h-10V80z"/>
          <path d="M205 110v10H125v-10z"/>
          <path d="M120 120h90v50h-90z"/>
          <path d="M115 170h100v15H115z"/>
        </g>
      </svg>
    ),

    // Myanmar 🇲🇲
    mm: (
      <svg viewBox="0 0 640 480" className={cn(sizeClasses[size], "rounded-sm shadow-sm", className)}>
        <path fill="#fecb00" d="M0 0h640v160H0z"/>
        <path fill="#34b233" d="M0 160h640v160H0z"/>
        <path fill="#ea2839" d="M0 320h640v160H0z"/>
        <g transform="translate(320 240)">
          <polygon fill="#fff" points="0,-140 33,-45 140,-45 55,17 88,111 0,55 -88,111 -55,17 -140,-45 -33,-45"/>
        </g>
      </svg>
    ),

    // Vietnam 🇻🇳
    vn: (
      <svg viewBox="0 0 640 480" className={cn(sizeClasses[size], "rounded-sm shadow-sm", className)}>
        <path fill="#da251d" d="M0 0h640v480H0z"/>
        <g transform="translate(320 240)">
          <polygon fill="#ff0" points="0,-130 30,-40 125,-40 50,15 80,105 0,50 -80,105 -50,15 -125,-40 -30,-40"/>
        </g>
      </svg>
    ),

    // United States 🇺🇸
    us: (
      <svg viewBox="0 0 640 480" className={cn(sizeClasses[size], "rounded-sm shadow-sm", className)}>
        <g fillRule="evenodd">
          <g strokeWidth="1pt">
            <path fill="#bd3d44" d="M0 0h640v37.5H0zm0 75.1h640v37.5H0zm0 75.1h640v37.5H0zm0 75.1h640v37.5H0zm0 75.1h640v37.5H0zm0 75.1h640V480H0z"/>
            <path fill="#fff" d="M0 37.5h640V75H0zm0 75.1h640v37.5H0zm0 75.1h640v37.5H0zm0 75.1h640v37.5H0zm0 75.1h640v37.5H0z"/>
          </g>
          <path fill="#192f5d" d="M0 0h260v260H0z"/>
        </g>
      </svg>
    ),
  }

  return flags[countryCode] || null
}
